import React from 'react';

import { getCryptoSymbol } from '@pkg/components';
import { type FloorGameCrypto } from '@pkg/floorGame';

import { Action, ExternalLink, InternalLink, Label, Price, Symbol, Value } from './styles';

export interface PriceActionProps {
    label: string;
    ariaLabel?: string;
    currency?: FloorGameCrypto;
    price?: number;
    compact?: boolean;
    disabled?: boolean;
    external?: boolean;
    url?: string;
    onAction?(): void;
}

export function PriceAction({
    label,
    ariaLabel,
    currency,
    price,
    compact = false,
    disabled = false,
    external = false,
    url,
    onAction,
}: PriceActionProps) {
    const currencySymbol = getCryptoSymbol(currency, true);
    const symbolMarkup = currencySymbol ? <Symbol>{currencySymbol}</Symbol> : null;

    const valueMarkup = price === undefined ? null : <Value>{price}</Value>;
    const priceMarkup =
        symbolMarkup || valueMarkup ? (
            <Price>
                {symbolMarkup}
                {valueMarkup}
            </Price>
        ) : null;

    const childrenMarkup = (
        <>
            <Label>{label}</Label>
            {priceMarkup}
        </>
    );

    if (url?.length) {
        return external ? (
            <ExternalLink $compact={compact} $disabled={disabled} href={url}>
                {childrenMarkup}
            </ExternalLink>
        ) : (
            <InternalLink $compact={compact} $disabled={disabled} to={url}>
                {childrenMarkup}
            </InternalLink>
        );
    }

    // `disabled` is repeated twice style props + actual attribute.
    const actionIsDisabled = Boolean(disabled || !onAction);

    return (
        <Action
            $compact={compact}
            $disabled={actionIsDisabled}
            disabled={actionIsDisabled}
            aria-label={ariaLabel}
            onClick={onAction}
        >
            {childrenMarkup}
        </Action>
    );
}
